import { memo, useState } from "react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import { IconTrashOutline18 as Trash } from "nucleo-ui-outline-18"

import {
	AlertDialog,
	Badge,
	Button,
	Dialog,
	Input,
	Switch,
} from "@productlane/ui"

import type { SpecSourceKind } from "./api-reference-source-fields"
import type { RouterOutputs } from "@/lib/api"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import { syncToast } from "./api-reference-page"
import {
	SpecFileButton,
	SpecSourceKindControl,
	uploadSpecFile,
} from "./api-reference-source-fields"
import { useDocsTabs } from "./use-docs-tabs"

type ApiReference = NonNullable<RouterOutputs["apiReference"]["get"]>

function relative(iso: string | null | undefined) {
	return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : "never"
}

const ROW = "py-3"

// A phone narrows the input beside it instead of wrapping the label.
const RowLabel = ({ children }: { children: string }) => (
	<span className="whitespace-nowrap">{children}</span>
)

/** Where the spec comes from, its docs path, past versions, and deleting the reference. */
export const ApiReferenceSettingsDialog = memo(() => {
	const { apiSettingsTabId: tabId, setApiSettingsTabId } = useDocsTabDialogs()
	const query = api.apiReference.get.useQuery(
		{ tabId: tabId ?? "" },
		{ enabled: !!tabId },
	)
	const utils = api.useUtils()
	const close = () => setApiSettingsTabId(null)
	const refresh = () => {
		if (tabId) void utils.apiReference.get.invalidate({ tabId })
	}
	const data = query.data

	return (
		<Dialog
			isOpen={!!tabId}
			onOpenChange={(open) => {
				if (!open) close()
			}}
			className="max-w-[34rem]"
			title={data ? `${data.name} settings` : "API reference settings"}
		>
			{!data ? (
				<p className="py-4 text-sm text-tertiary">
					{query.isLoading
						? "Loading…"
						: "This API reference no longer exists."}
				</p>
			) : (
				<div className="flex max-h-[70dvh] flex-col overflow-y-auto">
					<SourceRows data={data} onSaved={refresh} />
					<Versions data={data} onSaved={refresh} />
					<DeleteButton data={data} onDeleted={close} />
				</div>
			)}
		</Dialog>
	)
})

function useCanEdit() {
	const { role } = useRole()
	return !!role && role !== "VIEWER"
}

function SourceRows({
	data,
	onSaved,
}: {
	data: ApiReference
	onSaved: () => void
}) {
	const canEdit = useCanEdit()
	const state = data.state
	const savedUrl = state?.source.type === "url" ? state.source.url : ""
	const [kind, setKind] = useState<SpecSourceKind>(state?.source.type ?? "url")
	const [url, setUrl] = useState(savedUrl)
	const [slug, setSlug] = useState(data.slug ?? "")
	const [isUploading, setIsUploading] = useState(false)

	const update = api.apiReference.update.useMutation()
	const syncNow = api.apiReference.syncNow.useMutation()
	const uploadUrl = api.apiReference.uploadUrl.useMutation()
	const upload = api.apiReference.upload.useMutation()

	const save = async (
		input: Omit<Parameters<typeof update.mutateAsync>[0], "tabId">,
	) => {
		try {
			await update.mutateAsync({ tabId: data.id, ...input })
			onSaved()
			return true
		} catch (error) {
			toast.error({
				title: "Couldn't save",
				description: error instanceof Error ? error.message : undefined,
			})
			return false
		}
	}

	// A new URL is only useful once fetched, so saving one syncs it straight away.
	const saveUrl = async () => {
		const next = url.trim()
		if (!next || next === savedUrl) return
		if (!(await save({ url: next }))) return
		try {
			syncToast(await syncNow.mutateAsync({ tabId: data.id }))
		} catch (error) {
			toast.error({
				title: "Sync failed",
				description: error instanceof Error ? error.message : undefined,
			})
		}
		onSaved()
	}

	const uploadFile = async (file: File) => {
		setIsUploading(true)
		try {
			const uploaded = await uploadSpecFile(file, (input) =>
				uploadUrl.mutateAsync(input),
			)
			syncToast(await upload.mutateAsync({ tabId: data.id, ...uploaded }))
			onSaved()
		} catch (error) {
			toast.error({
				title: "Couldn't upload the spec",
				description: error instanceof Error ? error.message : undefined,
			})
		} finally {
			setIsUploading(false)
		}
	}

	if (!canEdit) return null

	return (
		<div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700/80">
			<SettingItem
				className={ROW}
				title={<RowLabel>Source</RowLabel>}
				action={
					<SpecSourceKindControl
						name="api-reference-source-kind"
						value={kind}
						onValueChange={setKind}
					/>
				}
			/>
			{kind === "url" ? (
				<SettingItem
					className={ROW}
					title={<RowLabel>OpenAPI URL</RowLabel>}
					action={
						<Input
							name="api-reference-url"
							aria-label="OpenAPI URL"
							value={url}
							onChange={(e) => setUrl(e.target.value)}
							onBlur={() => void saveUrl()}
							onKeyDown={(e) => {
								if (e.key === "Enter") e.currentTarget.blur()
							}}
							placeholder="https://api.example.com/openapi.json"
							className="w-72 max-sm:w-52"
						/>
					}
				/>
			) : (
				<SettingItem
					className={ROW}
					title={<RowLabel>OpenAPI file</RowLabel>}
					action={
						<SpecFileButton
							label={state?.source.type === "file" ? "Replace" : "Upload"}
							fileName={
								state?.source.type === "file" ? state.source.fileName : null
							}
							isLoading={isUploading}
							onFile={(file) => void uploadFile(file)}
						/>
					}
				/>
			)}
			{kind === "url" && state?.source.type === "url" && (
				<SettingItem
					className={ROW}
					title={<RowLabel>Sync every day</RowLabel>}
					action={
						<Switch
							name="api-reference-auto-sync"
							label={null}
							checked={state.autoSync}
							onCheckedChange={(checked) => void save({ autoSync: checked })}
						/>
					}
				/>
			)}
			<SettingItem
				className={ROW}
				title={<RowLabel>Docs path</RowLabel>}
				action={
					<Input
						name="api-reference-slug"
						aria-label="Docs path"
						prefix="/docs/"
						value={slug}
						onChange={(e) =>
							setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
						}
						onBlur={() => {
							if (slug && slug !== data.slug) {
								void save({ slug }).then((ok) => {
									if (!ok) setSlug(data.slug ?? "")
								})
							}
						}}
						maxLength={50}
						className="w-72 max-sm:w-52"
					/>
				}
			/>
		</div>
	)
}

function DeleteButton({
	data,
	onDeleted,
}: {
	data: ApiReference
	onDeleted: () => void
}) {
	const canEdit = useCanEdit()
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { setActiveTabId } = useDocsTabs()
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)
	const remove = api.apiReference.delete.useMutation()
	if (!canEdit) return null

	return (
		<div className="border-t border-gray-200 pt-3 dark:border-gray-700/80">
			<Button
				variant="ghost"
				size="sm"
				Icon={Trash}
				onClick={() => setIsDeleteOpen(true)}
				className="-ml-2 text-secondary"
			>
				Delete API reference
			</Button>
			<AlertDialog
				isOpen={isDeleteOpen}
				onOpenChange={setIsDeleteOpen}
				variant="positive"
				confirmText="Delete"
				title={`Delete "${data.name}"?`}
				description="Its pages go offline. Groups in the tab move back to Documentation."
				onCancel={() => setIsDeleteOpen(false)}
				onConfirm={async () => {
					try {
						await remove.mutateAsync({ tabId: data.id })
						onDeleted()
						setActiveTabId(null)
						// Deleting from Settings › Docs keeps the admin on the settings page.
						if (!pathname.startsWith("/settings"))
							void navigate({ to: "/edit-docs" })
					} catch (error) {
						toast.error({
							title: "Couldn't delete the API reference",
							description: error instanceof Error ? error.message : undefined,
						})
					}
					setIsDeleteOpen(false)
				}}
			/>
		</div>
	)
}

const SOURCE_LABELS: Record<string, string> = {
	first_import: "First import",
	daily_sync: "Daily sync",
	sync_now: "Synced by hand",
	upload: "File upload",
	restore: "Restored",
	publish_held: "Held changes published",
}

/** Each published version: what changed and when, with a way back to an older one. */
function Versions({
	data,
	onSaved,
}: {
	data: ApiReference
	onSaved: () => void
}) {
	const restore = api.apiReference.restore.useMutation({
		onSuccess: (result) => {
			if (result.status === "failed") {
				toast.error({ title: "Couldn't restore", description: result.error })
			} else {
				toast.success({ title: "Restored that version" })
			}
			onSaved()
		},
	})
	const state = data.state
	const rows = [
		...(state?.live ? [{ ...state.live, isLive: true }] : []),
		...(state?.history ?? []).map((row) => ({ ...row, isLive: false })),
	]
	if (rows.length === 0) return null

	return (
		<div className="flex flex-col border-t border-gray-200 pt-4 dark:border-gray-700/80">
			<h3 className="pb-1 text-sm font-medium text-primary">Versions</h3>
			<ul className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700/80">
				{rows.map((row) => (
					<li key={row.hash} className="flex h-11 items-center gap-3 text-sm">
						<span className="flex min-w-0 flex-none items-center gap-2 text-primary">
							{row.apiVersion || row.title}
							{row.isLive && (
								<Badge variant="green" size="sm">
									Live
								</Badge>
							)}
						</span>
						<span className="min-w-0 flex-1 truncate text-secondary">
							{row.summary || SOURCE_LABELS[row.source] || row.source}
						</span>
						<span className="flex-none text-[13px] text-tertiary">
							{relative(row.at)}
						</span>
						{!row.isLive && (
							<RoleCheck not={["VIEWER"]}>
								<Button
									variant="outline"
									size="sm"
									isLoading={
										restore.isLoading && restore.variables?.hash === row.hash
									}
									onClick={() =>
										restore.mutate({ tabId: data.id, hash: row.hash })
									}
								>
									Restore
								</Button>
							</RoleCheck>
						)}
					</li>
				))}
			</ul>
		</div>
	)
}
